/**
 * Licence aplikace – zkušební sezóna zdarma, potom trvalá nebo roční licence.
 *
 * Licenční kód vydává jen autor: obsah (jméno, druh, platnost) je podepsaný
 * jeho soukromým klíčem (ECDSA P-256). Aplikace má v sobě jen veřejný klíč,
 * takže kód ověří i bez internetu, ale nový kód si nikdo jiný nevyrobí.
 *
 * Tvar kódu: ULNIK-<obsah base64url>.<podpis base64url>
 */

/**
 * Zkušební sezóna: zdarma vždy do 31. 3. a vždy včetně jedné celé včelařské
 * sezóny – kdo začne do konce června, má zdarma do 31. 3. příštího roku,
 * kdo později, do 31. 3. roku přespříštího.
 */
export const TRIAL_END_MMDD = '03-31';
export const SEASON_CUTOFF_MMDD = '07-01';

export const PUBLIC_KEY: JsonWebKey = {
  kty: 'EC',
  crv: 'P-256',
  x: 'fD8wlbp7ZA4gvshqn5-JqxnuKQd5-HugP_VflphEZLQ',
  y: 'mEkexQETnvb34c4md40dOZjpA3DxDoIM7iHcpE1S3l4',
};

const ALG = { name: 'ECDSA', namedCurve: 'P-256' } as const;
const SIGN = { name: 'ECDSA', hash: 'SHA-256' } as const;
const PREFIX = 'ULNIK-';

export interface LicencePayload {
  v: 1;
  /** Jméno držitele */
  n: string;
  /** T = trvalá (jednorázová platba), R = roční (předplatné) */
  k: 'T' | 'R';
  /** Datum vydání YYYY-MM-DD */
  d: string;
  /** Platnost do (včetně) – jen u roční licence */
  e?: string;
}

export interface StoredLicence {
  code: string;
  payload: LicencePayload;
  activatedAt: string;
}

// base64url -----------------------------------------------------------------

function toB64(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromB64(s: string): Uint8Array<ArrayBuffer> {
  const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

const utf8 = (s: string) => new TextEncoder().encode(s);

// Podpis a ověření --------------------------------------------------------------

export async function signLicence(payload: LicencePayload, privateKey: JsonWebKey): Promise<string> {
  const key = await crypto.subtle.importKey('jwk', privateKey, ALG, false, ['sign']);
  const body = toB64(utf8(JSON.stringify(payload)));
  const sig = new Uint8Array(await crypto.subtle.sign(SIGN, key, utf8(body)));
  return `${PREFIX}${body}.${toB64(sig)}`;
}

/** Ověří kód; vrací obsah licence, nebo null, když kód není platný. */
export async function verifyLicence(code: string, publicKey: JsonWebKey = PUBLIC_KEY): Promise<LicencePayload | null> {
  const clean = code.replace(/\s+/g, '');
  if (!clean.toUpperCase().startsWith(PREFIX)) return null;
  const [body, sig] = clean.slice(PREFIX.length).split('.');
  if (!body || !sig) return null;
  try {
    const key = await crypto.subtle.importKey('jwk', publicKey, ALG, false, ['verify']);
    const ok = await crypto.subtle.verify(SIGN, key, fromB64(sig), utf8(body));
    if (!ok) return null;
    const p = JSON.parse(new TextDecoder().decode(fromB64(body))) as LicencePayload;
    if (p.v !== 1 || (p.k !== 'T' && p.k !== 'R') || typeof p.n !== 'string') return null;
    if (p.k === 'R' && !/^\d{4}-\d{2}-\d{2}$/.test(p.e ?? '')) return null;
    return p;
  } catch {
    return null;
  }
}

/** Zkontroluje, že soukromý klíč patří k veřejnému klíči v aplikaci. */
export async function privateKeyMatches(privateKey: JsonWebKey): Promise<boolean> {
  try {
    const code = await signLicence({ v: 1, n: 'test', k: 'T', d: '2000-01-01' }, privateKey);
    return (await verifyLicence(code)) !== null;
  } catch {
    return false;
  }
}

// Stav ----------------------------------------------------------------------------

export type LicenceStatus =
  | { kind: 'full'; name: string }
  | { kind: 'annual'; name: string; until: string; daysLeft: number }
  | { kind: 'trial'; until: string; daysLeft: number; annualExpired?: string }
  | { kind: 'expired'; since: string; annualExpired?: string };

const DAY = 86400000;
const addDays = (iso: string, n: number) => new Date(Date.parse(iso + 'T00:00:00Z') + n * DAY).toISOString().slice(0, 10);
const diffDays = (a: string, b: string) => Math.round((Date.parse(b + 'T00:00:00Z') - Date.parse(a + 'T00:00:00Z')) / DAY);

/** Poslední den zkušební sezóny. */
export function trialEnd(trialStart: string) {
  const y = Number(trialStart.slice(0, 4));
  return `${trialStart.slice(5) < SEASON_CUTOFF_MMDD ? y + 1 : y + 2}-${TRIAL_END_MMDD}`;
}

export function licenceStatus(trialStart: string, today: string, licence?: LicencePayload | null): LicenceStatus {
  if (licence?.k === 'T') return { kind: 'full', name: licence.n };
  if (licence?.k === 'R' && licence.e && today <= licence.e) {
    return { kind: 'annual', name: licence.n, until: licence.e, daysLeft: diffDays(today, licence.e) + 1 };
  }
  const annualExpired = licence?.k === 'R' ? licence.e : undefined;
  const end = trialEnd(trialStart);
  if (today <= end) return { kind: 'trial', until: end, daysLeft: diffDays(today, end) + 1, annualExpired };
  return { kind: 'expired', since: addDays(annualExpired && annualExpired > end ? annualExpired : end, 1), annualExpired };
}

export const canWrite = (s: LicenceStatus) => s.kind !== 'expired';

/** Den, kdy přestanou fungovat zápisy (nebo null u trvalé licence). */
export const validUntil = (s: LicenceStatus) => (s.kind === 'annual' || s.kind === 'trial' ? s.until : null);
